import React, { useEffect, useMemo, useState } from "react";

import { RightOutlined } from "@ant-design/icons";
import {
  Button, Input, Modal, Radio,
} from "antd";

import { SHIPPING_METHODS, buildTrackingRows } from "../utils";

import { ReactComponent as CheckboxCircleFillIcon } from "../../../../../assets/icons/Supply & Pickup/checkbox-circle-fill.svg";

import "./ConfigureShipmentModal.css";

const SHIPPING_METHOD_OPTIONS = [
  {
    key: SHIPPING_METHODS.BILLABLE_STAMPS,
    title: "Use Billable Stamps",
    description: "Enter the FedEx tracking number for each box.",
  },
  {
    key: SHIPPING_METHODS.GENERATE_LABELS,
    title: "Generate Shipping Labels",
    description: "Create and download shipping labels.",
  },
];

const STEPS = [
  { key: "shipping-method", label: "Shipping Method" },
  { key: "tracking-details", label: "Tracking Details" },
];

const ConfigureShipmentModal = ({
  open,
  onCancel,
  onContinue,
  boxes = [],
  initialMethod = null,
  initialTrackingIds = [],
  submitting = false,
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState(initialMethod);
  const [trackingRows, setTrackingRows] = useState(() => buildTrackingRows(boxes, initialTrackingIds));

  useEffect(() => {
    if (!open) return;
    setActiveStepIndex(0);
    setSelectedMethod(initialMethod);
    setTrackingRows(buildTrackingRows(boxes, initialTrackingIds));
    // Reset only when the modal opens so typing isn't wiped by parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const allTrackingFilled = useMemo(
    () => trackingRows.length > 0
      && trackingRows.every((row) => String(row.trackingId || "").trim().length > 0),
    [trackingRows],
  );

  const canContinue = !submitting && (
    activeStepIndex === 0
      ? Boolean(selectedMethod)
      : allTrackingFilled
  );

  const handleTrackingChange = (boxIndex, value) => {
    setTrackingRows((prev) => prev.map((row) => (
      row.boxIndex === boxIndex ? { ...row, trackingId: value } : row
    )));
  };

  const finish = async (trackingIds = []) => {
    await onContinue({
      shippingMethod: selectedMethod,
      trackingIds,
    });
  };

  const handleContinue = async () => {
    if (!canContinue) return;

    if (activeStepIndex === 0) {
      if (selectedMethod === SHIPPING_METHODS.BILLABLE_STAMPS) {
        setActiveStepIndex(1);
        return;
      }
      // Generate labels: submit immediately (no tracking step).
      await finish([]);
      return;
    }

    // Billable stamps: submit after tracking IDs are entered.
    await finish(trackingRows.map((row) => ({
      boxIndex: row.boxIndex,
      trackingId: String(row.trackingId || "").trim(),
    })));
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={submitting ? undefined : onCancel}
      footer={null}
      width={640}
      closable={false}
      maskClosable={false}
      destroyOnClose
      className="ConfigureShipmentModal"
    >
      <div className="ConfigureShipmentModal__Header">
        <h2 className="ConfigureShipmentModal__Title">Configure Your Shipment</h2>
        <p className="ConfigureShipmentModal__Subtitle">
          Choose how you&apos;d like to prepare your shipment before scheduling pickup
        </p>
      </div>

      <div className="ConfigureShipmentModal__Stepper">
        {STEPS.map((step, index) => {
          const isActive = index === activeStepIndex;
          const isPast = index < activeStepIndex;
          return (
            <React.Fragment key={step.key}>
              {index > 0 && (
                <RightOutlined className="ConfigureShipmentModal__StepperChevron" aria-hidden />
              )}
              <div
                className={[
                  "ConfigureShipmentModal__Step",
                  isActive ? "ConfigureShipmentModal__Step--active" : "",
                  isPast ? "ConfigureShipmentModal__Step--past" : "",
                ].filter(Boolean).join(" ")}
              >
                <span className="ConfigureShipmentModal__StepBadge">
                  {isPast ? (
                    <CheckboxCircleFillIcon
                      className="ConfigureShipmentModal__StepCompleteIcon"
                      aria-hidden
                    />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="ConfigureShipmentModal__StepLabel">{step.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="ConfigureShipmentModal__Body">
        {activeStepIndex === 0 ? (
          <>
            <h3 className="ConfigureShipmentModal__SectionTitle">
              Step 1 · Choose Shipping Method
            </h3>
            <p className="ConfigureShipmentModal__SectionSubtitle">
              Use existing stamps or create new shipping labels for this pickup
            </p>

            <p className="ConfigureShipmentModal__Question">
              How would you like to proceed with your shipment?
            </p>

            <Radio.Group
              className="ConfigureShipmentModal__Options"
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
            >
              {SHIPPING_METHOD_OPTIONS.map((option) => {
                const isSelected = selectedMethod === option.key;
                return (
                  <div
                    key={option.key}
                    className={[
                      "ConfigureShipmentModal__Option",
                      isSelected ? "ConfigureShipmentModal__Option--selected" : "",
                    ].filter(Boolean).join(" ")}
                    onClick={() => setSelectedMethod(option.key)}
                  >
                    <Radio value={option.key} className="ConfigureShipmentModal__Radio" />
                    <span className="ConfigureShipmentModal__OptionText">
                      <span className="ConfigureShipmentModal__OptionTitle">{option.title}</span>
                      <span className="ConfigureShipmentModal__OptionDescription">
                        {option.description}
                      </span>
                    </span>
                  </div>
                );
              })}
            </Radio.Group>
          </>
        ) : (
          <>
            <h3 className="ConfigureShipmentModal__SectionTitle">
              Step 2 · Enter Tracking IDs
            </h3>
            <p className="ConfigureShipmentModal__SectionSubtitle">
              Provide tracking IDs for each package if using Billable Stamps.
            </p>

            <div className="ConfigureShipmentModal__TrackingGrid">
              <div className="ConfigureShipmentModal__TrackingHeader">
                <span>Box</span>
                <span>Tracking ID</span>
              </div>
              {trackingRows.map((row) => (
                <div key={row.boxIndex} className="ConfigureShipmentModal__TrackingRow">
                  <Input
                    className="ConfigureShipmentModal__BoxField"
                    value={row.label}
                    readOnly
                    tabIndex={-1}
                  />
                  <Input
                    className="ConfigureShipmentModal__TrackingInput"
                    value={row.trackingId}
                    placeholder="Enter Tracking ID"
                    disabled={submitting}
                    onChange={(e) => handleTrackingChange(row.boxIndex, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="ConfigureShipmentModal__Footer">
        <Button
          type="default"
          className="ConfigureShipmentModal__CancelBtn"
          disabled={submitting}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          className="ConfigureShipmentModal__ContinueBtn"
          disabled={!canContinue}
          loading={submitting}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </div>
    </Modal>
  );
};

export default ConfigureShipmentModal;
